import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Menu, X, Mail, MapPin, Instagram, ArrowRight, Calendar, MessageCircle, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import "./i18n";

const CALENDLY_URL = "https://calendly.com/julie-openheartslisboa";
const WHATSAPP_NUMBER = "351932246240"; // Portuguese number without +
const EMAIL = "julie@openheartslisboa.org";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const languages = [
    { code: 'en', name: 'EN', flag: '🇬🇧' },
    { code: 'fr', name: 'FR', flag: '🇫🇷' },
    { code: 'pt', name: 'PT', flag: '🇵🇹' }
  ];

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setLangMenuOpen(false);
  };

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav 
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-[#FAF8F5]/95 backdrop-blur-sm py-4' : 'bg-transparent py-6'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex justify-between items-center">
          <Link 
            to="/" 
            data-testid="logo-link"
            className="text-sm lg:text-base uppercase tracking-[0.2em] text-[#2C2C2C]"
          >
            Journey to Self<span className="hidden sm:inline"> with Julie Aurora</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-10">
            <Link 
              to="/" 
              data-testid="nav-home"
              className={`nav-link ${isActive('/') ? 'active' : ''}`}
            >
              {t('nav.home')}
            </Link>
            <Link 
              to="/about" 
              data-testid="nav-about"
              className={`nav-link ${isActive('/about') ? 'active' : ''}`}
            >
              {t('nav.about')}
            </Link>
            <Link 
              to="/events" 
              data-testid="nav-events"
              className={`nav-link ${isActive('/events') ? 'active' : ''}`}
            >
              {t('nav.events')}
            </Link>
            <Link 
              to="/contact" 
              data-testid="nav-contact"
              className={`nav-link ${isActive('/contact') ? 'active' : ''}`}
            >
              {t('nav.contact')}
            </Link>
            
            {/* CONTAINER Logo Link */}
            <a
              href="https://www.containerforyou.org"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-container"
              className="flex items-center hover:opacity-80 transition-opacity"
              title="CONTAINER - For Personal Healing"
            >
              <img 
                src="https://customer-assets-4nw71qhi.emergentagent.net/job_d39c4215-8ec1-4c47-a275-634deefa1d3c/artifacts/22whqwcg_LOGO_02.webp"
                alt="CONTAINER"
                className="h-20 w-auto"
              />
            </a>
            
            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center gap-1 text-xs uppercase tracking-widest text-[#2C2C2C] hover:text-[#9BA88D] transition-colors"
                data-testid="lang-selector"
              >
                <Globe size={14} />
                {currentLang.name}
              </button>
              {langMenuOpen && (
                <div className="absolute top-full right-0 mt-2 bg-white shadow-lg border border-[#E8E4DD] py-1 min-w-[80px]">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full px-4 py-2 text-left text-xs uppercase tracking-widest hover:bg-[#E8E4DD] transition-colors ${
                        i18n.language === lang.code ? 'text-[#9BA88D]' : 'text-[#2C2C2C]'
                      }`}
                    >
                      {lang.flag} {lang.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button 
            data-testid="mobile-menu-button"
            className="md:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${isOpen ? 'open' : ''}`}>
        <Link 
          to="/" 
          data-testid="mobile-nav-home"
          onClick={() => setIsOpen(false)}
        >
          {t('nav.home')}
        </Link>
        <Link 
          to="/about" 
          data-testid="mobile-nav-about"
          onClick={() => setIsOpen(false)}
        >
          {t('nav.about')}
        </Link>
        <Link 
          to="/events" 
          data-testid="mobile-nav-events"
          onClick={() => setIsOpen(false)}
        >
          {t('nav.events')}
        </Link>
        <Link 
          to="/contact" 
          data-testid="mobile-nav-contact"
          onClick={() => setIsOpen(false)}
        >
          {t('nav.contact')}
        </Link>
        
        {/* CONTAINER Logo Link - Mobile */}
        <a
          href="https://www.containerforyou.org"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setIsOpen(false)}
          className="flex items-center justify-center mt-4"
        >
          <img 
            src="https://customer-assets-4nw71qhi.emergentagent.net/job_d39c4215-8ec1-4c47-a275-634deefa1d3c/artifacts/22whqwcg_LOGO_02.webp"
            alt="CONTAINER"
            className="h-16 w-auto"
          />
        </a>
        
        {/* Mobile Language Selector */}
        <div className="flex gap-4 mt-4 pt-4 border-t border-[#E8E4DD]">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => { changeLanguage(lang.code); setIsOpen(false); }}
              className={`text-sm ${i18n.language === lang.code ? 'text-[#9BA88D]' : 'text-[#2C2C2C]'}`}
            >
              {lang.flag} {lang.name}
            </button>
          ))}
        </div>
        
        <a 
          href={CALENDLY_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="mobile-nav-book"
          onClick={() => setIsOpen(false)}
          className="mt-4 px-8 py-3 bg-[#2C2C2C] text-[#FAF8F5] text-sm uppercase tracking-widest"
        >
          {t('nav.bookSession')}
        </a>
      </div>
    </>
  );
};

// Footer Component
const Footer = () => {
  const { t } = useTranslation();
  
  return (
    <footer data-testid="footer" className="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          <div>
            <h3 className="font-serif text-2xl mb-1 uppercase tracking-wider">{t('footer.title')}</h3>
            <p className="text-lg font-serif mb-2">{t('footer.subtitle')}</p>
            <p className="text-sm opacity-70 leading-relaxed">
              {t('footer.description')}<br />
              {t('footer.description2')}
            </p>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-widest mb-4 opacity-60">{t('footer.locationTitle')}</h4>
            <p className="text-sm opacity-70 flex items-start gap-2">
              <MapPin size={16} className="mt-1 flex-shrink-0" />
              {t('footer.locationText')}<br />
              {t('footer.locationText2')}<br />
              {t('footer.locationText3')}
            </p>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-widest mb-4 opacity-60">{t('footer.connectTitle')}</h4>
            <Link 
              to="/contact"
              data-testid="footer-contact"
              className="text-sm opacity-70 flex items-center gap-2 mb-2 hover:opacity-100 transition-opacity"
            >
              <Mail size={16} />
              {t('footer.sendMessage')}
            </Link>
            <a 
              href="https://instagram.com/juliethedivine" 
              data-testid="footer-instagram"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm opacity-70 flex items-center gap-2 hover:opacity-100 transition-opacity"
            >
              <Instagram size={16} />
              @juliethedivine
            </a>
          </div>
        </div>
        <div className="border-t border-[#FAF8F5]/20 pt-8 text-center">
          <p className="text-xs opacity-50">
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </p>
        </div>
      </div>
    </footer>
  );
};

// Practice Card Component with expandable description
const PracticeCard = ({ practice, index }) => {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();
  
  return (
    <div 
      className="practice-card animate-fade-in"
      style={{ animationDelay: `${index * 0.1}s` }}
      data-testid={`practice-card-${index}`}
    >
      <h3 className="font-serif">{practice.title}</h3>
      <p className="mb-4">{practice.shortDesc}</p>
      {expanded && (
        <div className="text-sm opacity-80 whitespace-pre-line border-t border-[#E8E4DD] pt-4 mt-4">
          {practice.fullDesc}
        </div>
      )}
      <button 
        onClick={() => setExpanded(!expanded)}
        className="text-xs uppercase tracking-widest mt-4 text-[#9BA88D] hover:text-[#2C2C2C] transition-colors"
        data-testid={`practice-toggle-${index}`}
      >
        {expanded ? t('home.readLess') : t('home.readMore')}
      </button>
    </div>
  );
};

// Home Page
const Home = () => {
  const { t } = useTranslation();
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    // Use real client testimonials
    setTestimonials([
      { id: "1", text: t('testimonials.t1'), author: "Alix", location: "" },
      { id: "2", text: t('testimonials.t2'), author: "Daniel", location: "" },
      { id: "3", text: t('testimonials.t3'), author: "Maria", location: "" },
      { id: "4", text: t('testimonials.t4'), author: "Ana", location: "" }
    ]);
  }, [t]);

  const practices = [
    {
      title: t('practices.spinalFlow.title'),
      shortDesc: t('practices.spinalFlow.short'),
      fullDesc: t('practices.spinalFlow.full')
    },
    {
      title: t('practices.energyHealing.title'),
      shortDesc: t('practices.energyHealing.short'),
      fullDesc: t('practices.energyHealing.full')
    },
    {
      title: t('practices.flowMassage.title'),
      shortDesc: t('practices.flowMassage.short'),
      fullDesc: t('practices.flowMassage.full')
    },
    {
      title: t('practices.soundHealing.title'),
      shortDesc: t('practices.soundHealing.short'),
      fullDesc: t('practices.soundHealing.full')
    },
    {
      title: t('practices.alteredStates.title'),
      shortDesc: t('practices.alteredStates.short'),
      fullDesc: t('practices.alteredStates.full')
    }
  ];

  return (
    <main data-testid="home-page">
      {/* Hero Section */}
      <section data-testid="hero-section" className="min-h-screen flex items-center pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <div className="animate-fade-in">
              <span className="section-label">{t('home.welcome')}</span>
              <h1 className="hero-title font-serif mb-6">
                {t('home.heroTitle1')}<br />{t('home.heroTitle2')}
              </h1>
              <p className="hero-subtitle max-w-lg mb-8">
                {t('home.heroSubtitle')}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/contact"
                  className="btn-primary inline-flex items-center gap-2" 
                  data-testid="hero-cta-book"
                >
                  {t('nav.bookSession')}
                </Link>
                <Link to="/about" className="btn-outline" data-testid="hero-cta-about">
                  {t('home.learnMore')}
                </Link>
              </div>
            </div>
            <div className="animate-fade-in stagger-2 img-container">
              <img 
                src="https://customer-assets.emergentagent.com/job_0bc97243-e11a-4dd4-9512-eee4f484008b/artifacts/aashc5iv_VoltaNov2023-352.jpg"
                alt="Julie Aurora Boulanger"
                className="w-full h-[500px] lg:h-[600px] object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Practices Section */}
      <section data-testid="practices-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Introduction Text with Image */}
          <div className="mb-20">
            <span className="section-label">{t('home.myJourney')}</span>
            <h2 className="section-title font-serif mb-12">
              {t('home.journeyTitle')}
            </h2>
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div className="img-container">
                <img 
                  src="https://customer-assets.emergentagent.com/job_0bc97243-e11a-4dd4-9512-eee4f484008b/artifacts/5ukivalr_Screenshot%202026-03-15%20at%2012.29.10.png"
                  alt="The healing space at the farm"
                  className="w-full h-[500px] object-cover"
                />
              </div>
              <div className="space-y-4 text-lg leading-relaxed opacity-80">
                <p>{t('home.journeyText1')}</p>
                <p>{t('home.journeyText2')}</p>
                <p>{t('home.journeyText3')}</p>
              </div>
            </div>
          </div>

          {/* The Offering */}
          <div className="mb-16">
            <span className="section-label">{t('home.theOffering')}</span>
            <h2 className="section-title font-serif">
              {t('home.offeringTitle')}
            </h2>
            <p className="text-lg opacity-70">
              {t('home.offeringSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
            {practices.slice(0, 4).map((practice, index) => (
              <PracticeCard key={index} practice={practice} index={index} />
            ))}
            {/* Image card in the middle of second row */}
            <div className="practice-card p-0 overflow-hidden h-fit">
              <img 
                src="https://customer-assets.emergentagent.com/job_0bc97243-e11a-4dd4-9512-eee4f484008b/artifacts/ofeu101a_IMG-20250923-WA0047.jpg"
                alt="Fire ceremony at the farm"
                className="w-full h-auto object-contain"
              />
            </div>
            {practices.slice(4).map((practice, index) => (
              <PracticeCard key={index + 4} practice={practice} index={index + 4} />
            ))}
          </div>

          {/* Session Info */}
          <div className="mt-16 p-8 bg-[#E8E4DD]">
            <p className="text-lg leading-relaxed mb-6">
              {t('home.sessionInfo')}
            </p>
            <a 
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="practices-cta-book"
            >
              <Calendar size={16} />
              {t('home.bookDiscoveryCall')}
            </a>
          </div>
        </div>
      </section>

      {/* Quote Section with Image */}
      <section className="py-24 bg-[#E8E4DD]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="img-container">
              <img 
                src="https://customer-assets.emergentagent.com/job_0bc97243-e11a-4dd4-9512-eee4f484008b/artifacts/p29sscu6_0S0A3700.jpg"
                alt="Julie Aurora Boulanger practicing"
                className="w-full h-[500px] object-cover object-center"
              />
            </div>
            <div className="text-center lg:text-left">
              <blockquote className="font-serif text-2xl lg:text-3xl italic leading-relaxed text-[#2C2C2C] mb-6">
                "{t('home.quote')}"
              </blockquote>
              <div className="swiss-divider lg:mx-0 mx-auto"></div>
              <p className="text-sm uppercase tracking-widest opacity-60 mt-6">Julie Aurora Boulanger</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section data-testid="testimonials-section" className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-16">
            <span className="section-label">{t('home.feedbacks')}</span>
            <h2 className="section-title font-serif whitespace-nowrap">
              {t('home.whatClientsSay')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={testimonial.id} 
                className="testimonial-card animate-fade-in"
                style={{ animationDelay: `${index * 0.15}s` }}
                data-testid={`testimonial-${index}`}
              >
                <p className="testimonial-text">"{testimonial.text}"</p>
                <p className="testimonial-author">
                  — {testimonial.author}{testimonial.location && `, ${testimonial.location}`}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#2C2C2C] text-[#FAF8F5]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-serif text-4xl lg:text-5xl mb-6">
            {t('home.readyToMeet')}
          </h2>
          <p className="text-lg opacity-70 mb-10 max-w-2xl mx-auto">
            {t('home.ctaSubtitle')}
          </p>
          <Link 
            to="/contact"
            data-testid="cta-book-session"
            className="inline-flex items-center gap-2 bg-[#FAF8F5] text-[#2C2C2C] px-8 py-4 text-sm uppercase tracking-widest hover:bg-white transition-colors"
          >
            {t('nav.bookSession')} <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
};

// About Page
const About = () => {
  const { t } = useTranslation();

  return (
    <main data-testid="about-page" className="pt-24">
      {/* Hero */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-start">
            <div>
              <span className="section-label">{t('nav.about')}</span>
              <h1 className="hero-title font-serif mb-6">
                {t('about.title')}
              </h1>
              <div className="swiss-divider"></div>
              <div className="space-y-4">
                <p className="text-lg leading-relaxed opacity-80">{t('about.bioTop1')}</p>
                <p className="text-lg leading-relaxed opacity-80">{t('about.bioTop2')}</p>
              </div>
            </div>
            <div className="img-container">
              <img 
                src="https://customer-assets.emergentagent.com/job_0bc97243-e11a-4dd4-9512-eee4f484008b/artifacts/14gre12w_Screenshot%202026-03-19%20at%2008.48.06.png"
                alt="Julie Aurora Boulanger"
                className="w-full h-[550px] lg:h-[700px] object-cover object-top"
              />
            </div>
          </div>
          {/* Full width continuation of bio */}
          <div className="mt-6 space-y-4">
            <p className="text-lg leading-relaxed opacity-80">{t('about.bioBottom1')}</p>
            <p className="text-lg leading-relaxed opacity-80">{t('about.bioBottom2')}</p>
            <p className="text-lg leading-relaxed opacity-80">{t('about.bioBottom3')}</p>
            <p className="text-lg leading-relaxed mt-6">
              <a 
                href="https://containerforyou.org" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#9BA88D] hover:text-[#2C2C2C] underline underline-offset-4 transition-colors"
              >
                containerforyou.org
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Certifications & Practice Image */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-12">
            <div className="p-8 bg-[#E8E4DD] lg:w-1/2">
              <h3 className="font-serif text-2xl mb-6">{t('about.certifications')}</h3>
              <ul className="space-y-3 text-sm mb-8">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-[#9BA88D] rounded-full mt-2 flex-shrink-0"></span>
                  {t('about.cert1')}
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-[#9BA88D] rounded-full mt-2 flex-shrink-0"></span>
                  {t('about.cert2')}
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-[#9BA88D] rounded-full mt-2 flex-shrink-0"></span>
                  {t('about.cert3')}
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-[#9BA88D] rounded-full mt-2 flex-shrink-0"></span>
                  {t('about.cert4')}
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-[#9BA88D] rounded-full mt-2 flex-shrink-0"></span>
                  {t('about.cert5')}
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-[#9BA88D] rounded-full mt-2 flex-shrink-0"></span>
                  {t('about.cert6')}
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-[#9BA88D] rounded-full mt-2 flex-shrink-0"></span>
                  {t('about.cert7')}
                </li>
              </ul>
              {/* Certificate Images */}
              <div className="grid grid-cols-3 gap-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_0bc97243-e11a-4dd4-9512-eee4f484008b/artifacts/hyg6tu9a_Screenshot%202026-03-15%20at%2012.17.21.png"
                  alt="Sobada Maya Certificate"
                  className="w-full h-auto object-cover rounded shadow-sm"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_0bc97243-e11a-4dd4-9512-eee4f484008b/artifacts/przpyilr_Screenshot%202026-03-15%20at%2012.14.08.png"
                  alt="Spinal Flow Technique Certificate"
                  className="w-full h-auto object-cover rounded shadow-sm"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_0bc97243-e11a-4dd4-9512-eee4f484008b/artifacts/sxzvmzy7_Screenshot%202026-03-15%20at%2012.17.41.png"
                  alt="Heart Protocol Facilitator Certificate"
                  className="w-full h-auto object-cover rounded shadow-sm"
                />
              </div>
            </div>
            <div className="lg:w-1/2 overflow-hidden self-stretch">
              <img 
                src="https://customer-assets.emergentagent.com/job_0bc97243-e11a-4dd4-9512-eee4f484008b/artifacts/7v8doyyc_0S0A3694.jpg"
                alt="Julie Aurora practicing Spinal Flow"
                className="w-full h-full object-cover object-[center_20%]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Location - The Farm */}
      <section className="py-24 bg-[#E8E4DD]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_0bc97243-e11a-4dd4-9512-eee4f484008b/artifacts/pg17ebiz_DJI_0143.jpg"
              alt="The Farm in Rio de Mouro - Aerial View"
              className="w-full h-[400px] object-cover"
            />
            <div>
              <span className="section-label">{t('about.theSpace')}</span>
              <h2 className="section-title font-serif">
                {t('about.farmTitle')}
              </h2>
              <p className="text-lg leading-relaxed mb-6">
                {t('about.farmText1')}
              </p>
              <p className="text-lg leading-relaxed opacity-80">
                {t('about.farmText2')}
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

// Events Page
const Events = () => {
  const { t } = useTranslation();

  const events = [
    {
      id: 1,
      title: t('event1.title'),
      date: t('event1.date'),
      time: t('event1.time'),
      image: "https://customer-assets.emergentagent.com/job_0bc97243-e11a-4dd4-9512-eee4f484008b/artifacts/bj03oec6_1.jpg",
      intro: t('event1.intro'),
      description: [
        t('event1.description1'),
        t('event1.description2'),
        t('event1.description3'),
        t('event1.description4'),
        t('event1.description5'),
        t('event1.description6')
      ],
      activities: [
        t('event1.activity1'),
        t('event1.activity2'),
        t('event1.activity3'),
        t('event1.activity4'),
        t('event1.activity5'),
        t('event1.activity6'),
        t('event1.activity7'),
        t('event1.activity8'),
        t('event1.activity9'),
        t('event1.activity10')
      ],
      details: {
        language: t('event1.languageValue'),
        participants: t('event1.participantsValue'),
        location: t('event1.locationValue'),
        price: t('event1.priceValue')
      },
      whatToBring: [
        t('event1.bring1'),
        t('event1.bring2'),
        t('event1.bring3'),
        t('event1.bring4'),
        t('event1.bring5')
      ]
    }
  ];

  return (
    <main data-testid="events-page" className="pt-24">
      {/* Hero */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <span className="section-label">{t('events.sectionLabel')}</span>
          <h1 className="hero-title font-serif mb-6">
            {t('events.title')}
          </h1>
          <p className="text-xl leading-relaxed opacity-80 max-w-3xl">
            {t('events.subtitle')}
          </p>
        </div>
      </section>

      {/* Events List */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {events.map((event) => (
            <div key={event.id} className="mb-16">
              {/* Event Header */}
              <div className="grid lg:grid-cols-2 gap-12 items-start mb-12">
                <div>
                  <div className="img-container mb-8">
                    <img 
                      src={event.image}
                      alt={event.title}
                      className="w-full max-h-[500px] object-contain object-left"
                    />
                  </div>
                </div>
                <div>
                  <span className="section-label">{event.date} | {event.time}</span>
                  <h2 className="font-serif text-3xl lg:text-4xl mb-4">{event.title}</h2>
                  <p className="text-lg leading-relaxed mb-6 opacity-80">{event.intro}</p>
                  
                  {/* Details Box */}
                  <div className="bg-[#E8E4DD] p-6 mb-6">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium">{t('events.language')}</span>
                        <p className="opacity-70">{event.details.language}</p>
                      </div>
                      <div>
                        <span className="font-medium">{t('events.participants')}</span>
                        <p className="opacity-70">{event.details.participants}</p>
                      </div>
                      <div>
                        <span className="font-medium">{t('events.location')}</span>
                        <p className="opacity-70">{event.details.location}</p>
                      </div>
                      <div>
                        <span className="font-medium">{t('events.investment')}</span>
                        <p className="opacity-70">{event.details.price}</p>
                      </div>
                    </div>
                  </div>

                  <a 
                    href="https://docs.google.com/forms/d/e/1FAIpQLSemp_jVPZz5qqpGhiLpKKQUh7DTDpA_fdjS1GvjKkuQlLX4hA/viewform"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    {t('events.reserveSpot')}
                  </a>
                </div>
              </div>

              {/* Event Description - Two columns aligned */}
              <div className="grid lg:grid-cols-2 gap-12">
                <div>
                  <h3 className="font-serif text-2xl mb-4">{t('events.aboutThisDay')}</h3>
                  <div className="space-y-4 opacity-80">
                    {event.description.map((paragraph, index) => (
                      <p key={index} className="leading-relaxed">{paragraph}</p>
                    ))}
                  </div>
                  {/* Inez Instagram */}
                  <div className="mt-6 flex items-center gap-4">
                    <span className="text-sm font-medium">{t('events.followInez')}</span>
                    <a 
                      href="https://instagram.com/portadores_de_fogo"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-[#9BA88D] hover:text-[#2C2C2C] transition-colors flex items-center gap-1"
                    >
                      <Instagram size={14} />
                      @portadores_de_fogo
                    </a>
                    <a 
                      href="https://instagram.com/yellowsmileystar"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-[#9BA88D] hover:text-[#2C2C2C] transition-colors flex items-center gap-1"
                    >
                      <Instagram size={14} />
                      @yellowsmileystar
                    </a>
                  </div>
                </div>
                <div>
                  <h3 className="font-serif text-2xl mb-4">{t('events.experience')}</h3>
                  <ul className="space-y-2 mb-8">
                    {event.activities.map((activity, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <span className="w-1.5 h-1.5 bg-[#9BA88D] rounded-full mt-2 flex-shrink-0"></span>
                        <span className="opacity-80">{activity}</span>
                      </li>
                    ))}
                  </ul>

                  <h3 className="font-serif text-2xl mb-4">{t('events.whatToBring')}</h3>
                  <ul className="space-y-2">
                    {event.whatToBring.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <span className="w-1.5 h-1.5 bg-[#9BA88D] rounded-full mt-2 flex-shrink-0"></span>
                        <span className="opacity-80">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </main>
  );
};

// Contact Page
const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <main data-testid="contact-page" className="pt-24 min-h-screen flex items-center">
        <div className="max-w-2xl mx-auto px-6 lg:px-12 text-center">
          <h1 className="font-serif text-4xl mb-6">{t('contact.thankYou')}</h1>
          <p className="text-lg opacity-70 mb-8">
            {t('contact.messageReceived')}
          </p>
          <Link to="/" className="btn-outline">
            {t('contact.returnHome')}
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="contact-page" className="pt-24">
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <span className="section-label">{t('nav.contact')}</span>
            <h1 className="hero-title font-serif mb-6">
              {t('contact.title')}
            </h1>
            <p className="text-xl leading-relaxed opacity-80 max-w-2xl mx-auto">
              {t('contact.subtitle')}
            </p>
          </div>

          {/* Contact Options */}
          <div className="grid sm:grid-cols-3 gap-6 max-w-3xl mx-auto mb-16">
            <a 
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-calendly"
              className="p-6 bg-white border border-[#E8E4DD] hover:border-[#2C2C2C] transition-colors group"
            >
              <Calendar className="w-8 h-8 mx-auto mb-4 text-[#9BA88D] group-hover:text-[#2C2C2C] transition-colors" />
              <h3 className="font-serif text-xl mb-2 text-center">{t('contact.scheduleCall')}</h3>
              <p className="text-sm opacity-60 text-center">{t('contact.scheduleDesc')}</p>
            </a>

            <a 
              href={`https://wa.me/${WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp"
              className="p-6 bg-white border border-[#E8E4DD] hover:border-[#2C2C2C] transition-colors group"
            >
              <MessageCircle className="w-8 h-8 mx-auto mb-4 text-[#9BA88D] group-hover:text-[#2C2C2C] transition-colors" />
              <h3 className="font-serif text-xl mb-2 text-center">{t('contact.whatsapp')}</h3>
              <p className="text-sm opacity-60 text-center">+351 932 246 240</p>
            </a>

            <a 
              href="https://instagram.com/juliethedivine"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-instagram"
              className="p-6 bg-white border border-[#E8E4DD] hover:border-[#2C2C2C] transition-colors group"
            >
              <Instagram className="w-8 h-8 mx-auto mb-4 text-[#9BA88D] group-hover:text-[#2C2C2C] transition-colors" />
              <h3 className="font-serif text-xl mb-2 text-center">{t('contact.instagram')}</h3>
              <p className="text-sm opacity-60 text-center">@juliethedivine</p>
            </a>
          </div>

          {/* Contact Form */}
          <div className="bg-white p-8 lg:p-12 max-w-2xl mx-auto">
            <h2 className="font-serif text-2xl mb-8 text-center">{t('contact.sendMessage')}</h2>
            <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('contact.yourName')}
                  required
                  data-testid="input-name"
                  className="form-input"
                />
              </div>
              <div>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder={t('contact.subject')}
                  required
                  data-testid="input-subject"
                  className="form-input"
                />
              </div>
              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t('contact.yourMessage')}
                  required
                  data-testid="input-message"
                  className="form-input form-textarea"
                />
              </div>
              <button 
                type="submit" 
                data-testid="submit-button"
                disabled={loading}
                className="btn-primary w-full disabled:opacity-50"
              >
                {loading ? t('contact.sending') : t('contact.send')}
              </button>
            </form>
          </div>

          {/* Location */}
          <div className="flex items-center justify-center gap-2 text-sm opacity-60 mt-12">
            <MapPin size={16} />
            <span>{t('contact.location')}</span>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Navigation />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/events" element={<Events />} />
          <Route path="/contact" element={<Contact />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
